import { TreeItems, isSuccess, useDynamicConfig, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { preferenceService, usePreferencesQuery } from "../shared/api/preferences";
import { Preference, PreferenceIndexResource } from "../shared/types";

interface PreferenceContextState {
	emptyPreference: Preference;
	handleDrop: (newTree: TreeItems) => Promise<void>;
	preferencesTree: PreferenceIndexResource[];
}

const baseEmptyPreference: Preference = {
	id: -1,
	name: "",
	parentId: null,
	translations: [],
};

interface Props {
	children: React.ReactNode;
}

const PreferenceContext = React.createContext<PreferenceContextState | undefined>(undefined);

export function PreferenceProvider({ children }: Props) {
	const { languageSettings } = useDynamicConfig();
	const { t } = useTranslation();
	const { data } = usePreferencesQuery();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const emptyPreference: Preference = useMemo(
		() => ({
			...baseEmptyPreference,
			translations: [
				{
					locale: languageSettings.fallback,
					label: "",
				},
			],
		}),
		[languageSettings.fallback],
	);

	const handleDrop = useCallback(
		async (newTree: TreeItems) => {
			const response = await preferenceService.sort(newTree);

			if (isSuccess(response)) {
				showToast({
					type: "success",
					title: t("Preferences"),
					body: t("Preferences were successfully sorted."),
				});
				void queryClient.invalidateQueries({ queryKey: preferenceService.allKey });
			}
		},
		[queryClient, showToast, t],
	);

	return (
		data && (
			<PreferenceContext.Provider
				value={{
					emptyPreference,
					preferencesTree: data,
					handleDrop,
				}}
			>
				{children}
			</PreferenceContext.Provider>
		)
	);
}

export const usePreference = () => {
	const context = useContext(PreferenceContext);

	if (context === undefined) {
		throw new Error(`usePreference needs to be wrapped in a PreferenceProvider.`);
	}

	return context;
};
